import React, { useState, useEffect } from 'react';
import {
   Box,
   Paper,
   Typography,
   Grid,
   Card,
   CardContent,
   CardActions,
   Button,
   Chip,
   IconButton,
   Menu,
   MenuItem,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogContentText,
   DialogActions,
   Divider,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   CircularProgress,
   Alert,
   Tooltip,
} from '@mui/material';
import {
   MoreVert as MoreVertIcon,
   Edit as EditIcon,
   Delete as DeleteIcon,
   Visibility as VisibilityIcon,
   VisibilityOff as VisibilityOffIcon,
   CheckCircle as CheckCircleIcon,
   Event as EventIcon,
   Discount as DiscountIcon,
   Lock as LockIcon,
   Star as StarIcon,
   Support as SupportIcon,
   PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import {
   collection,
   getDocs,
   doc,
   updateDoc,
   deleteDoc,
   getDoc,
   orderBy,
   query,
   where,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { SubscriptionTier } from '../index';
import SubscriptionTierForm from './SubscriptionTierForm';
import { formatCurrency } from '../../../../utils/formatting';

interface SubscriptionTierListProps {
   organizationId: string;
   onRefreshData: () => void;
}

const SubscriptionTierList: React.FC<SubscriptionTierListProps> = ({
   organizationId,
   onRefreshData,
}) => {
   // State
   const [tiers, setTiers] = useState<any[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
   const [editMode, setEditMode] = useState(false);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [deletingTier, setDeletingTier] = useState(false);

   // Menu state
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const menuOpen = Boolean(anchorEl);

   // Fetch tiers on component mount
   useEffect(() => {
      fetchTiers();
   }, [organizationId]);

   // Fetch subscription tiers from Firestore
   const fetchTiers = async () => {
      try {
         setLoading(true);
         setError(null);

         const tiersRef = collection(db, 'subscription_tiers');
         // Sort by createdAt timestamp
         const tiersQuery = query(
            tiersRef,
            where('orgId', '==', organizationId),
            orderBy('createdAt', 'desc')
         );
         const snapshot = await getDocs(tiersQuery);

         if (snapshot.empty) {
            setTiers([]);
            setLoading(false);
            return;
         }

         const fetchedTiers: SubscriptionTier[] = snapshot.docs.map(
            (doc) =>
               ({
                  id: doc.id,
                  ...doc.data(),
               }) as SubscriptionTier
         );

         setTiers(fetchedTiers);
      } catch (err) {
         console.error('Error fetching subscription tiers:', err);
         setError('Failed to load subscription tiers. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   // Handle menu open
   const handleMenuClick = (event: React.MouseEvent<HTMLElement>, tier: SubscriptionTier) => {
      setAnchorEl(event.currentTarget);
      setSelectedTier(tier);
   };

   // Handle menu close
   const handleMenuClose = () => {
      setAnchorEl(null);
   };

   // Handle edit tier
   const handleEditTier = () => {
      setEditMode(true);
      handleMenuClose();
   };

   // Handle toggle tier visibility
   const handleToggleTierVisibility = async () => {
      if (!selectedTier) return;

      try {
         const tierRef = doc(
            db,
            `organizations/${organizationId}/subscriptionTiers/${selectedTier.id}`
         );
         await updateDoc(tierRef, {
            active: !selectedTier.active,
         });

         // Update local state
         setTiers(
            tiers.map((tier) =>
               tier.id === selectedTier.id ? { ...tier, active: !tier.active } : tier
            )
         );

         // Refresh data from parent
         onRefreshData();
      } catch (err) {
         console.error('Error toggling tier visibility:', err);
         setError('Failed to update tier. Please try again.');
      }

      handleMenuClose();
   };

   // Open delete confirmation dialog
   const handleDeleteConfirmOpen = () => {
      setDeleteDialogOpen(true);
      handleMenuClose();
   };

   // Cancel delete
   const handleDeleteCancel = () => {
      setDeleteDialogOpen(false);
   };

   // Confirm delete
   const handleDeleteConfirm = async () => {
      if (!selectedTier) return;

      try {
         setDeletingTier(true);

         // Check if tier has subscribers before deleting
         const subscribersRef = collection(db, `organizations/${organizationId}/subscribers`);
         const subscribersQuery = query(subscribersRef, where('tierId', '==', selectedTier.id));
         const subscribersSnapshot = await getDocs(subscribersQuery);

         if (!subscribersSnapshot.empty) {
            setError(
               'Cannot delete this tier as it has active subscribers. Please move or remove subscribers first.'
            );
            setDeletingTier(false);
            setDeleteDialogOpen(false);
            return;
         }

         // Delete the tier
         const tierRef = doc(
            db,
            `organizations/${organizationId}/subscriptionTiers/${selectedTier.id}`
         );
         await deleteDoc(tierRef);

         // Update local state
         setTiers(tiers.filter((tier) => tier.id !== selectedTier.id));

         // Refresh data from parent
         onRefreshData();

         setDeleteDialogOpen(false);
      } catch (err) {
         console.error('Error deleting tier:', err);
         setError('Failed to delete tier. Please try again.');
      } finally {
         setDeletingTier(false);
      }
   };

   // Handle edit form submit success
   const handleEditSuccess = () => {
      setEditMode(false);
      fetchTiers();
      onRefreshData();
   };

   // Return to list from edit mode
   const handleCancelEdit = () => {
      setEditMode(false);
      setSelectedTier(null);
   };

   // If in edit mode, show the form
   if (editMode && selectedTier) {
      return (
         <SubscriptionTierForm
            organizationId={organizationId}
            tier={selectedTier}
            onCancel={handleCancelEdit}
            onSuccess={handleEditSuccess}
         />
      );
   }

   // Empty state
   if (!loading && tiers.length === 0) {
      return (
         <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Box sx={{ py: 4 }}>
               <Typography variant='h6' gutterBottom>
                  No Subscription Tiers Yet
               </Typography>
               <Typography variant='body1' color='text.secondary' paragraph>
                  Create your first tier to start offering subscriptions to your members.
               </Typography>
            </Box>
         </Paper>
      );
   }

   return (
      <>
         {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
               <CircularProgress />
            </Box>
         ) : (
            <Grid container spacing={3}>
               {tiers.map((tier) => (
                  <Grid item xs={12} md={6} lg={4} key={tier.id}>
                     <Card
                        sx={{
                           height: '100%',
                           display: 'flex',
                           flexDirection: 'column',
                           position: 'relative',
                           opacity: tier.active ? 1 : 0.7,
                           borderColor: tier.active ? 'primary.main' : 'divider',
                        }}
                        variant='outlined'
                     >
                        {!tier.active && (
                           <Chip
                              label='Inactive'
                              color='default'
                              size='small'
                              sx={{
                                 position: 'absolute',
                                 top: 10,
                                 right: 10,
                                 zIndex: 1,
                              }}
                           />
                        )}

                        <CardContent sx={{ flexGrow: 1 }}>
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'flex-start',
                              }}
                           >
                              <Typography variant='h5' component='div' gutterBottom>
                                 {tier?.title}
                              </Typography>
                              <IconButton onClick={(e) => handleMenuClick(e, tier)} size='small'>
                                 <MoreVertIcon fontSize='small' />
                              </IconButton>
                           </Box>

                           <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                              <Typography variant='h4' component='div'>
                                 {formatCurrency(tier.price || 0)}
                              </Typography>
                              <Typography variant='body2' color='text.secondary' sx={{ ml: 1 }}>
                                 /month
                              </Typography>
                           </Box>

                           <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                              {tier.description}
                           </Typography>

                           <Typography variant='body2' sx={{ mb: 2 }}>
                              {formatCurrency(tier.price || 0)}/{tier.type}
                              {tier.type === 'annual' &&
                                 tier.price &&
                                 tier.price < tier.monthlyPrice * 12 && (
                                    <Chip
                                       label={`Save ${Math.round(100 - (tier.price / (tier.monthlyPrice * 12)) * 100)}%`}
                                       color='success'
                                       size='small'
                                       sx={{ ml: 1 }}
                                    />
                                 )}
                           </Typography>

                           <Divider sx={{ my: 2 }} />

                           <Typography variant='subtitle2' gutterBottom>
                              Benefits:
                           </Typography>

                           <List dense>
                              {tier.benefits?.earlyAccess && (
                                 <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                       <EventIcon color='primary' fontSize='small' />
                                    </ListItemIcon>
                                    <ListItemText primary='Early Access to Events' />
                                 </ListItem>
                              )}

                              {tier.benefits?.discounts && (
                                 <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                       <DiscountIcon color='primary' fontSize='small' />
                                    </ListItemIcon>
                                    <ListItemText primary='Exclusive Discounts' />
                                 </ListItem>
                              )}

                              {tier.benefits?.privateEvents && (
                                 <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                       <LockIcon color='primary' fontSize='small' />
                                    </ListItemIcon>
                                    <ListItemText primary='Private Events' />
                                 </ListItem>
                              )}

                              {tier.benefits?.exclusiveContent && (
                                 <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                       <StarIcon color='primary' fontSize='small' />
                                    </ListItemIcon>
                                    <ListItemText primary='Exclusive Content' />
                                 </ListItem>
                              )}

                              {tier.benefits?.prioritySupport && (
                                 <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: '30px' }}>
                                       <SupportIcon color='primary' fontSize='small' />
                                    </ListItemIcon>
                                    <ListItemText primary='Priority Support' />
                                 </ListItem>
                              )}

                              {Object.keys(tier.benefits || {}).length > 0 &&
                                 Object.keys(tier.benefits).map((benefit) => {
                                    if (
                                       benefit != 'discounts' &&
                                       benefit != 'prioritySupport' &&
                                       benefit != 'exclusiveContent' &&
                                       benefit != 'privateEvents' &&
                                       benefit != 'earlyAccess'
                                    ) {
                                       return (
                                          <ListItem disablePadding key={benefit}>
                                             <ListItemIcon sx={{ minWidth: '30px' }}>
                                                <CheckCircleIcon color='primary' fontSize='small' />
                                             </ListItemIcon>
                                             <ListItemText primary={benefit} />
                                          </ListItem>
                                       );
                                    }
                                 })}

                              {/* {tier.benefits?.customBenefit1 && (
                      <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: '30px' }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={tier.benefits.customBenefit1} />
                      </ListItem>
                    )}
                    
                    {tier.benefits?.customBenefit2 && (
                      <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: '30px' }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={tier.benefits.customBenefit2} />
                      </ListItem>
                    )} */}
                           </List>

                           {!tier.unlimitedMembers && (
                              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                                 <PeopleAltIcon fontSize='small' color='action' sx={{ mr: 1 }} />
                                 <Typography variant='body2' color='text.secondary'>
                                    Limited to {tier.memberLimit} members
                                 </Typography>
                              </Box>
                           )}
                        </CardContent>

                        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                           <Button
                              size='small'
                              variant='outlined'
                              onClick={() => {
                                 setSelectedTier(tier);
                                 setEditMode(true);
                              }}
                              startIcon={<EditIcon />}
                           >
                              Edit
                           </Button>

                           <Tooltip title={tier.active ? 'Deactivate tier' : 'Activate tier'}>
                              <IconButton
                                 color={tier.active ? 'primary' : 'default'}
                                 onClick={() => {
                                    setSelectedTier(tier);
                                    handleToggleTierVisibility();
                                 }}
                              >
                                 {tier.active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                              </IconButton>
                           </Tooltip>
                        </CardActions>
                     </Card>
                  </Grid>
               ))}
            </Grid>
         )}

         {/* Action menu */}
         <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
            <MenuItem onClick={handleEditTier}>
               <ListItemIcon>
                  <EditIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText>Edit</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleToggleTierVisibility}>
               <ListItemIcon>
                  {selectedTier?.active ? (
                     <VisibilityOffIcon fontSize='small' />
                  ) : (
                     <VisibilityIcon fontSize='small' />
                  )}
               </ListItemIcon>
               <ListItemText>{selectedTier?.active ? 'Deactivate' : 'Activate'}</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleDeleteConfirmOpen}>
               <ListItemIcon>
                  <DeleteIcon fontSize='small' />
               </ListItemIcon>
               <ListItemText>Delete</ListItemText>
            </MenuItem>
         </Menu>

         {/* Delete confirmation dialog */}
         <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
            <DialogTitle>Delete Subscription Tier</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Are you sure you want to delete the "{selectedTier?.name}" subscription tier? This
                  action cannot be undone.
               </DialogContentText>
               <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
                  Note: You can only delete tiers that have no active subscribers.
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel} disabled={deletingTier}>
                  Cancel
               </Button>
               <Button
                  onClick={handleDeleteConfirm}
                  color='error'
                  disabled={deletingTier}
                  startIcon={deletingTier ? <CircularProgress size={20} /> : <DeleteIcon />}
               >
                  Delete
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
};

export default SubscriptionTierList;
